import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import axios from "axios";
import useDialog from "../../hooks/useDialog";

export default function Settings() {
  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useDialog();

  useEffect(() => {
    // Load initial data from local storage
    const storedName = localStorage.getItem("name");
    if (storedName) setName(storedName);
  }, []);

  const validatePassword = (pass: string) => {
    if (pass.length < 8) return "პაროლი უნდა შედგებოდეს მინიმუმ 8 სიმბოლოსგან";
    if (!/[A-Z]/.test(pass)) return "პაროლი უნდა შეიცავდეს მინიმუმ 1 დიდ ასოს";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pass)) return "პაროლი უნდა შეიცავდეს მინიმუმ 1 სიმბოლოს";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password) {
      const passwordError = validatePassword(password);
      if (passwordError) {
        showSnackbar(passwordError, "error");
        return;
      }
      if (!oldPassword) {
        showSnackbar("გთხოვთ მიუთითოთ ძველი პაროლი", "error");
        return;
      }
      if (password !== confirmPassword) {
        showSnackbar("პაროლები არ ემთხვევა", "error");
        return;
      }
    }

    setLoading(true);

    try {
      const response = await axios.put("/auth/profile", {
        name,
        password: password || undefined,
        oldPassword: oldPassword || undefined,
      });

      showSnackbar(response.data.message, "success");
      
      // Update local storage if name changed
      if (name) {
        localStorage.setItem("name", name);
      }
      
      // Clear password fields
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error(error);
      const errorMessage = error.response?.data?.message || "დაფიქსირდა შეცდომა";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Card variant="outlined">
        <CardHeader
          title="პარამეტრები"
          titleTypographyProps={{ variant: "h5", fontWeight: 600 }}
        />
        <Divider />
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  პირადი ინფორმაცია
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="სახელი"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                  required
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  პაროლის შეცვლა
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  დატოვეთ ცარიელი თუ არ გსურთ პაროლის შეცვლა
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ძველი პაროლი"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  variant="outlined"
                  required={!!password}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ახალი პაროლი"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="გაიმეორეთ პაროლი"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  disabled={!password}
                  required={!!password}
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  size="large"
                >
                  შენახვა
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
